/**
 * The reader's user-visible strings (UX-WORD-02), kept apart from `strings.ts` so the reader can change on its own.
 * Same conventions: plain constants and small functions, no DOM; none may contain a word matched by `BANNED_WORDS`
 * (UX-WORD-01, checked by test/unit/reader-strings.test.ts). Voice: plain, short, specific.
 */

export const READER = {
	title: 'Reader',
	empty: 'This document has no paragraphs to show.',
	region: 'Document text with evidence marks',
	paragraph: (n: string, total: string) => `Paragraph ${n} of ${total}`,
	position: (a: string, b: string, total: string) => `Paragraphs ${a}–${b} of ${total}`,
	sceneBreak: 'Scene break',
	sceneBreakGlyph: '· · ·',
	flaggedNote: (label: string) => `Flagged stretch: ${label}`,
	bracketLabel: (id: string, what: string) => `${id} · ${what}`,
	kindWord: {
		hotspot: 'hotspot',
		elevated: 'elevated',
		spike: 'single paragraph',
		unit: 'chapter',
	},
	noFocusHint: 'No channels are focused. Choose up to three to see their marks alone.',
	withheldHint: (ids: string) =>
		`Hotspot flags are withheld for ${ids}; their marks show without brackets.`,
} as const;

export const TOOLBAR = {
	label: 'Reader: evidence map',
	layer: 'Layer',
	layerAll: 'All channels',
	layerFocused: 'Focused',
	layerFocusedCount: (n: number) => `Focused (${n})`,
	channels: 'Channels',
	channelsCount: (n: number) => `Channels (${n})`,
	pickerLabel: 'Choose channels to focus',
	search: 'Search channels by ID or name',
	searchPlaceholder: 'ID or name',
	noMatch: 'No channel matches.',
	focusCap: 'At most 3 channels can be focused; the oldest choice was replaced.',
	dismiss: 'Dismiss',
	clearFocus: 'Clear focus',
	removeFocus: (id: string) => `Remove ${id} from the focus`,
	legendSlot: (n: number, id: string, name: string, style: string) =>
		`Slot ${n}: ${id} ${name}, ${style} underline`,
	styleSolid: 'solid',
	styleDashed: 'dashed',
	styleDotted: 'dotted',
	legendAll: {
		one: '1 channel',
		two: '2 channels',
		three: '3 or more',
		zero: 'evidence only (weight 0)',
	},
	prevEvidence: '◂ Evidence',
	nextEvidence: 'Evidence ▸',
	prevFlagged: '◂ Flagged',
	nextFlagged: 'Flagged ▸',
	prevEvidenceLabel: 'Previous evidence span',
	nextEvidenceLabel: 'Next evidence span',
	prevFlaggedLabel: 'Previous flagged stretch',
	nextFlaggedLabel: 'Next flagged stretch',
	chapter: 'Chapter',
	chapterPlaceholder: 'Go to a chapter',
	chapterOption: (n: number, title: string | undefined) => `${n} ${title ?? `Chapter ${n}`}`,
	shortcuts: 'Keyboard shortcuts',
	shortcutsHelp:
		'j and k: paragraphs · n and N: evidence · h and H: flagged stretches · Enter: inspect · Escape: close · g then a chapter number',
	evidenceMenu: 'Evidence',
	find: 'Find in text',
	findPlaceholder: 'Find in text',
	findNext: 'Next match',
	findPrev: 'Previous match',
	findCount: (i: string, n: string) => `${i} of ${n}`,
	findNone: 'No matches',
	findMore: (shown: string, total: string) => `${shown} of ${total} matches shown`,
	findClear: 'Clear search',
} as const;

export const ANNOUNCE = {
	evidence: (id: string, feature: string, weight: string, p: string, n: string) =>
		`${id}, ${feature}, weight ${weight}, paragraph ${p} of ${n}`,
	noMoreEvidence: 'No more evidence in this direction.',
	flagged: (id: string, what: string, a: string, b: string) =>
		`${id}, ${what}, paragraphs ${a}–${b}`,
	noMoreFlagged: 'No more flagged stretches in this direction.',
	noFlagged: 'There are no flagged stretches for this layer.',
	chapter: (n: number, title: string | undefined) => `Chapter ${n}${title ? `: ${title}` : ''}`,
	noChapter: (n: string) => `There is no chapter ${n}.`,
	paragraph: (p: string, n: string) => `Paragraph ${p} of ${n}`,
	inspectorOpened: 'Span inspector opened.',
	inspectorClosed: 'Span inspector closed.',
	gPending: (digits: string) => `Go to chapter ${digits || '…'}`,
} as const;

export const INSPECTOR = {
	title: 'Evidence at this point',
	label: 'Span inspector',
	close: 'Close inspector',
	where: (p: string, n: string) => `Paragraph ${p} of ${n}`,
	coverCount: (spans: number, channels: number) =>
		`${spans} ${spans === 1 ? 'span' : 'spans'} from ${channels} ${channels === 1 ? 'channel' : 'channels'}`,
	none: 'No evidence spans cover this point.',
	feature: 'Feature',
	weight: (w: string) => `weight ${w}`,
	weightZero: 'weight 0 (evidence only)',
	flagged: 'Flagged stretches containing this point',
	noFlagged: 'No flagged stretch of this channel contains this point.',
	flagLine: (kind: string, flag: string, a: string, b: string) =>
		`${flag} ${kind} · paragraphs ${a}–${b}`,
	/** The rest of a flagged-stretch line after its flag word (the flag word itself is a glossary Term). */
	flagRest: (kind: string, a: string, b: string) => ` ${kind} · paragraphs ${a}–${b}`,
	scoreLine: (score: string, interval: string) => `score ${score} · 90% interval ${interval}`,
	withheld: (reason: string) => `Flags withheld. ${reason}`,
	focusThis: 'Focus this channel',
	focused: 'In the focus',
	openChannel: 'Open channel',
	kindSegment: 'segment',
	kindSpike: 'single paragraph',
	kindUnit: 'chapter',
	focusedTag: 'focused',
} as const;

export const RAIL = {
	label: 'Chapters',
	title: 'Chapters',
	chapterLabel: (n: number, title: string | undefined) => `${n} ${title ?? `Chapter ${n}`}`,
} as const;
